
import React, { useRef, useEffect, useCallback } from 'react';
import { Message as MessageType, Language } from '../../types';
import { TRANSLATIONS } from '../../constants';
import Message from '../Message';

interface ChatMessagesUIProps {
  messages: MessageType[];
  isLoading: boolean;
  voice: string;
  speechSpeed: number;
  language: Language;
  apiKey?: string;
  onOpenSettingsAt: (tab: 'profile' | 'app' | 'security') => void;
}

const ChatMessagesUI: React.FC<ChatMessagesUIProps> = ({ 
  messages, isLoading, voice, speechSpeed, language, apiKey, onOpenSettingsAt 
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const t = TRANSLATIONS[language];
  const isRtl = language === 'ar';

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading, scrollToBottom]);

  // Check if API key is truly missing (both user config and env)
  const isConfigRequired = !apiKey && !process.env.API_KEY;

  return (
    <div className="flex-1 flex flex-col min-h-0 bg-white dark:bg-[#0f0f13]">
      <div className="flex-1 overflow-y-auto p-4 lg:p-10 space-y-6 lg:space-y-8 custom-scrollbar scroll-smooth">
        {messages.length === 0 && !isConfigRequired && (
          <div className="h-full flex flex-col items-center justify-center text-center space-y-6 opacity-80 animate-in fade-in zoom-in-95 duration-700">
            <div className="relative">
               <div className="absolute inset-0 bg-emerald-500/20 blur-2xl rounded-full"></div>
               <div className="relative w-20 h-20 lg:w-28 lg:h-28 bg-white dark:bg-emerald-500 rounded-[34px] flex items-center justify-center text-emerald-500 dark:text-white text-3xl lg:text-4xl shadow-2xl border border-slate-100 dark:border-white/10">
                <img src="logo.png" style={{ width: '70px', height: '70px' }} alt="Logo" />
               </div>
            </div>
            <div className="space-y-2">
              <h3 className="text-lg lg:text-2xl font-black text-slate-800 dark:text-white">{t.noMessages}</h3>
              <p className="text-[11px] lg:text-[12px] text-slate-500 dark:text-slate-400 font-bold max-w-xs mx-auto leading-relaxed">{t.welcomeText}</p>
            </div>
          </div>
        )}

        {isConfigRequired && messages.length === 0 && (
          <div className="h-full flex flex-col items-center justify-center text-center px-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
             <div className="w-20 h-20 bg-amber-500/10 rounded-[2rem] flex items-center justify-center text-amber-500 text-3xl mb-6 shadow-xl border border-amber-500/20">
                <i className="fas fa-key-skeleton animate-pulse"></i>
             </div>
             <h3 className="text-xl font-black text-slate-800 dark:text-white mb-2">
                {isRtl ? 'تهيئة مطلوبة' : 'Configuration Required'}
             </h3>
             <p className="text-xs text-slate-500 dark:text-slate-400 font-bold max-w-sm mb-8 leading-relaxed">
                {isRtl 
                   ? 'لبدء استخدام المساعد الذكي، يجب عليك إضافة مفتاح الربط (API Key) الخاص بك في إعدادات الأمان.' 
                   : 'To start using the smart assistant, you must add your Gemini API Key in the security settings.'}
             </p>
             <button 
                onClick={() => onOpenSettingsAt('security')}
                className="group flex items-center gap-3 bg-slate-900 dark:bg-white text-white dark:text-slate-900 px-8 py-3.5 rounded-2xl font-black text-xs shadow-xl transition-all hover:scale-105 active:scale-95"
             >
                <i className="fas fa-barcode text-emerald-500 group-hover:animate-bounce"></i>
                {isRtl ? 'الذهاب لإعدادات الأمان' : 'Go to Security Settings'}
             </button>
          </div>
        )}

        {messages.map((msg) => <Message key={msg.id} message={msg} voice={voice} speechSpeed={speechSpeed} />)}
        
        {isLoading && (
          <div className={`flex ${isRtl ? 'justify-start' : 'justify-end'} animate-in fade-in`}>
            <div className="bg-slate-50 dark:bg-white/[0.03] px-5 py-3 rounded-2xl flex items-center gap-3 border border-slate-100 dark:border-white/5">
              <div className="flex gap-1.5"><div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-bounce"></div><div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-bounce [animation-delay:0.2s]"></div><div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-bounce [animation-delay:0.4s]"></div></div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} className="h-4" />
      </div>
    </div>
  );
};

export default ChatMessagesUI;
